import { createFileRoute } from "@tanstack/react-router"
import { Pencil, Plus, Trash2 } from "lucide-react"
import { useState } from "react"

import { StatusAlert } from "@/components/shared/status-alert"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { FormPageSkeleton } from "@/components/ui/page-skeletons"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  trackConfigurationSchema,
  type TrackConfiguration,
  type TrackDefinition,
} from "@/core/activity-levels/track-catalog"
import {
  useAddRewardMutation,
  useRemoveRewardMutation,
  useRewardsQuery,
  useTracksQuery,
  useUpdateTrackMutation,
} from "@/hooks/activity-levels"

export const Route = createFileRoute("/_authed/_admin/activity-levels/")({
  component: ActivityLevelsTracksRoute,
})

const toConfiguration = (track: TrackDefinition): TrackConfiguration => ({
  activityKey: track.activityKey,
  xpMin: track.xpMin,
  xpMax: track.xpMax,
  cooldownSeconds: track.cooldownSeconds,
  dailyXpCap: track.dailyXpCap,
  isEnabled: track.isEnabled,
  rewardPolicy: track.rewardPolicy,
  levelUpChannelId: track.levelUpChannelId,
  settings: {
    allowedChannelIds: [...track.settings.allowedChannelIds],
    noXpRoleIds: [...track.settings.noXpRoleIds],
    minMessageLength: track.settings.minMessageLength,
    channelXpMultipliers: { ...track.settings.channelXpMultipliers },
    roleXpMultipliers: { ...track.settings.roleXpMultipliers },
    lengthEffort: { ...track.settings.lengthEffort },
  },
})

function ActivityLevelsTracksRoute() {
  const tracksQuery = useTracksQuery()
  const updateTrack = useUpdateTrackMutation()
  const track = tracksQuery.data?.find(
    (candidate) => candidate.activityKey === "message",
  )
  const [draft, setDraft] = useState<TrackConfiguration | null>(null)
  const [error, setError] = useState<string | null>(null)

  if (tracksQuery.isLoading) return <FormPageSkeleton />
  if (tracksQuery.isError || !track) {
    return (
      <StatusAlert tone="danger">
        Unable to load Message XP configuration
      </StatusAlert>
    )
  }

  const update = <K extends keyof TrackConfiguration>(
    key: K,
    value: TrackConfiguration[K],
  ) => setDraft((current) => (current ? { ...current, [key]: value } : current))

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <CardTitle>
              {track.emoji} {track.label}
            </CardTitle>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Edit Message XP settings"
              onClick={() => {
                setDraft(toConfiguration(track))
                setError(null)
              }}
            >
              <Pencil aria-hidden="true" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          {track.isEnabled ? "Enabled" : "Disabled"} · {track.xpMin}–
          {track.xpMax} XP · {track.cooldownSeconds}s cooldown
        </CardContent>
      </Card>
      <Dialog
        open={draft !== null}
        onOpenChange={(open) => {
          if (!open) setDraft(null)
        }}
      >
        <DialogContent className="flex h-[calc(100dvh-2rem)] max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-5xl flex-col gap-0 overflow-hidden p-0 sm:h-auto sm:max-w-5xl">
          <DialogHeader className="shrink-0 border-b px-5 py-4 pr-12 sm:px-6">
            <DialogTitle>Edit Message XP</DialogTitle>
            <p className="text-sm text-muted-foreground">
              Configure how members earn Message XP and progress through levels.
            </p>
          </DialogHeader>
          {draft && (
            <form
              className="flex min-h-0 flex-1 flex-col"
              onSubmit={(event) => {
                event.preventDefault()
                const parsed = trackConfigurationSchema.safeParse(draft)
                if (!parsed.success) {
                  setError(
                    parsed.error.issues[0]?.message ?? "Invalid configuration",
                  )
                  return
                }
                setError(null)
                updateTrack.mutate(parsed.data, {
                  onSuccess: () => {
                    setDraft(null)
                  },
                  onError: (cause) =>
                    setError(
                      cause instanceof Error
                        ? cause.message
                        : "Unable to save configuration",
                    ),
                })
              }}
            >
              <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-5 py-5 sm:px-6">
                {error && <StatusAlert tone="danger">{error}</StatusAlert>}
                <fieldset className="grid gap-4 rounded-xl border border-border/60 p-4">
                  <legend className="text-sm font-medium">Earning rules</legend>
                  <p className="text-sm text-muted-foreground">
                    Set the XP range, rate limits, and cooldown for each
                    message.
                  </p>
                  <Label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={draft.isEnabled}
                      onChange={(event) =>
                        update("isEnabled", event.target.checked)
                      }
                    />
                    Enabled
                  </Label>
                  <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                    <NumberInput
                      label="Minimum XP"
                      value={draft.xpMin}
                      onChange={(value) => update("xpMin", value)}
                    />
                    <NumberInput
                      label="Maximum XP"
                      value={draft.xpMax}
                      onChange={(value) => update("xpMax", value)}
                    />
                    <NumberInput
                      label="Cooldown seconds"
                      value={draft.cooldownSeconds}
                      onChange={(value) => update("cooldownSeconds", value)}
                    />
                    <NumberInput
                      label="Daily XP cap (0 is unlimited)"
                      value={draft.dailyXpCap}
                      onChange={(value) => update("dailyXpCap", value)}
                    />
                  </div>
                </fieldset>

                <fieldset className="grid gap-4 rounded-xl border border-border/60 p-4">
                  <legend className="text-sm font-medium">Progression</legend>
                  <p className="text-sm text-muted-foreground">
                    Choose where level-ups appear and how earned reward roles
                    stack.
                  </p>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Label className="grid gap-1">
                      Level-up channel ID
                      <Input
                        value={draft.levelUpChannelId ?? ""}
                        onChange={(event) =>
                          update(
                            "levelUpChannelId",
                            event.target.value.trim() === ""
                              ? null
                              : event.target.value,
                          )
                        }
                        placeholder="optional"
                      />
                    </Label>
                    <Label className="grid gap-1">
                      Reward policy
                      <Select
                        value={draft.rewardPolicy}
                        onValueChange={(value) => {
                          if (value === "stack" || value === "replace")
                            update("rewardPolicy", value)
                        }}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="stack">
                            Stack — keep every earned reward role
                          </SelectItem>
                          <SelectItem value="replace">
                            Replace — keep only the highest reward tier
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </Label>
                  </div>
                </fieldset>

                <fieldset className="grid gap-4 rounded-xl border border-border/60 p-4">
                  <legend className="text-sm font-medium">Eligibility</legend>
                  <p className="text-sm text-muted-foreground">
                    Limit which messages and members can earn XP.
                  </p>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <NumberInput
                      label="Minimum message length"
                      value={draft.settings.minMessageLength}
                      onChange={(value) =>
                        update("settings", {
                          ...draft.settings,
                          minMessageLength: value,
                        })
                      }
                    />
                  </div>
                  <div className="grid gap-6 lg:grid-cols-2">
                    <IdListInput
                      legend="Allowed channels"
                      description="Only messages in these channels earn XP. Leave empty to pause Message XP."
                      itemLabel="Channel"
                      value={draft.settings.allowedChannelIds}
                      onChange={(allowedChannelIds) =>
                        update("settings", {
                          ...draft.settings,
                          allowedChannelIds,
                        })
                      }
                    />
                    <IdListInput
                      legend="Excluded roles"
                      description="Members with any of these roles do not earn Message XP."
                      itemLabel="Role"
                      value={draft.settings.noXpRoleIds}
                      onChange={(noXpRoleIds) =>
                        update("settings", {
                          ...draft.settings,
                          noXpRoleIds,
                        })
                      }
                    />
                  </div>
                </fieldset>

                <fieldset className="grid gap-4 rounded-xl border border-border/60 p-4">
                  <legend className="text-sm font-medium">XP bonuses</legend>
                  <p className="text-sm text-muted-foreground">
                    Increase or reduce XP for specific channels and roles.
                  </p>
                  <div className="grid gap-6 lg:grid-cols-2">
                    <MultiplierListInput
                      legend="Channel XP bonuses"
                      description="Use 1 for normal XP, 2 for double XP, or 0.5 for half XP."
                      itemLabel="Channel"
                      value={draft.settings.channelXpMultipliers}
                      onChange={(channelXpMultipliers) =>
                        update("settings", {
                          ...draft.settings,
                          channelXpMultipliers,
                        })
                      }
                    />
                    <MultiplierListInput
                      legend="Role XP bonuses"
                      description="When multiple channel and role bonuses apply, the highest bonus wins."
                      itemLabel="Role"
                      value={draft.settings.roleXpMultipliers}
                      onChange={(roleXpMultipliers) =>
                        update("settings", {
                          ...draft.settings,
                          roleXpMultipliers,
                        })
                      }
                    />
                  </div>
                </fieldset>

                <fieldset className="grid gap-4 rounded-xl border border-border/60 p-4">
                  <legend className="text-sm font-medium">
                    Message length bonus
                  </legend>
                  <p className="text-sm text-muted-foreground">
                    Award extra XP for longer messages. Set the extra multiplier
                    to 0 to disable it.
                  </p>
                  <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                    <NumberInput
                      label="Characters per bonus step"
                      value={draft.settings.lengthEffort.charactersPerStep}
                      onChange={(value) =>
                        update("settings", {
                          ...draft.settings,
                          lengthEffort: {
                            ...draft.settings.lengthEffort,
                            charactersPerStep: value,
                          },
                        })
                      }
                    />
                    <NumberInput
                      label="Extra XP multiplier per step"
                      value={draft.settings.lengthEffort.xpMultiplierPerStep}
                      onChange={(value) =>
                        update("settings", {
                          ...draft.settings,
                          lengthEffort: {
                            ...draft.settings.lengthEffort,
                            xpMultiplierPerStep: value,
                          },
                        })
                      }
                    />
                    <NumberInput
                      label="Maximum bonus steps"
                      value={draft.settings.lengthEffort.maxSteps}
                      onChange={(value) =>
                        update("settings", {
                          ...draft.settings,
                          lengthEffort: {
                            ...draft.settings.lengthEffort,
                            maxSteps: value,
                          },
                        })
                      }
                    />
                  </div>
                </fieldset>
              </div>
              <DialogFooter className="mx-0 mb-0 shrink-0 border-t bg-muted/50 px-5 py-4 sm:px-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDraft(null)}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={updateTrack.isPending}>
                  Save changes
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
      <RewardsEditor />
    </div>
  )
}

function RewardsEditor() {
  const rewardsQuery = useRewardsQuery("message")
  const addReward = useAddRewardMutation("message")
  const removeReward = useRemoveRewardMutation("message")
  const [level, setLevel] = useState("")
  const [roleId, setRoleId] = useState("")
  const rewards = rewardsQuery.data ?? []

  return (
    <Card>
      <CardHeader>
        <CardTitle>Level rewards</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-4">
        <div className="flex flex-wrap items-end gap-2">
          <Label className="grid gap-1">
            Level
            <Input
              type="number"
              value={level}
              onChange={(event) => setLevel(event.target.value)}
              className="w-24"
            />
          </Label>
          <Label className="grid gap-1">
            Role ID
            <Input
              value={roleId}
              onChange={(event) => setRoleId(event.target.value)}
              className="w-64"
              placeholder="Discord role snowflake"
            />
          </Label>
          <Button
            disabled={
              level.trim() === "" || roleId.trim() === "" || addReward.isPending
            }
            onClick={() =>
              addReward.mutate(
                { level: Number(level), roleId: roleId.trim() },
                {
                  onSuccess: () => {
                    setLevel("")
                    setRoleId("")
                  },
                },
              )
            }
          >
            <Plus className="mr-2 size-4" />
            Add reward
          </Button>
        </div>
        {rewards.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No level rewards for this track yet.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Level</TableHead>
                <TableHead>Role ID</TableHead>
                <TableHead aria-label="actions" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rewards.map((reward) => (
                <TableRow key={`${reward.level}-${reward.roleId}`}>
                  <TableCell className="font-medium">{reward.level}</TableCell>
                  <TableCell className="font-mono text-xs">
                    {reward.roleId}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={removeReward.isPending}
                      onClick={() =>
                        removeReward.mutate({
                          level: reward.level,
                          roleId: reward.roleId,
                        })
                      }
                    >
                      <Trash2 className="mr-2 size-4" />
                      Remove
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  )
}

function NumberInput({
  label,
  value,
  onChange,
}: {
  label: string
  value: number
  onChange: (value: number) => void
}) {
  return (
    <Label className="grid gap-1">
      {label}
      <Input
        type="number"
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
      />
    </Label>
  )
}

function IdListInput({
  legend,
  description,
  itemLabel,
  value,
  onChange,
}: {
  legend: string
  description: string
  itemLabel: string
  value: string[]
  onChange: (value: string[]) => void
}) {
  const [id, setId] = useState("")
  const add = () => {
    const normalized = id.trim()
    if (normalized === "" || value.includes(normalized)) return

    onChange([...value, normalized])
    setId("")
  }

  return (
    <fieldset className="grid gap-2">
      <legend className="text-sm font-medium">{legend}</legend>
      <p className="text-sm text-muted-foreground sm:min-h-10">{description}</p>
      {value.map((current, index) => (
        <div
          key={current}
          className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_8rem] sm:items-end"
        >
          <Label className="grid flex-1 gap-1">
            {itemLabel} {index + 1}
            <Input value={current} readOnly />
          </Label>
          <Button
            type="button"
            variant="destructive"
            size="sm"
            className="h-11 w-full"
            onClick={() =>
              onChange(
                value.filter((_, currentIndex) => currentIndex !== index),
              )
            }
          >
            <Trash2 className="mr-2 size-4" />
            Remove
          </Button>
        </div>
      ))}
      <div className="mt-2 grid gap-2 sm:grid-cols-[minmax(0,1fr)_8rem] sm:items-end">
        <Input
          aria-label={`New ${itemLabel.toLowerCase()}`}
          value={id}
          placeholder={`Discord ${itemLabel.toLowerCase()} ID`}
          onChange={(event) => setId(event.target.value)}
        />
        <Button
          type="button"
          disabled={id.trim() === "" || value.includes(id.trim())}
          className="h-11 w-full"
          onClick={add}
        >
          <Plus className="mr-2 size-4" />
          Add {itemLabel.toLowerCase()}
        </Button>
      </div>
    </fieldset>
  )
}

function MultiplierListInput({
  legend,
  description,
  itemLabel,
  value,
  onChange,
}: {
  legend: string
  description: string
  itemLabel: string
  value: Record<string, number>
  onChange: (value: Record<string, number>) => void
}) {
  const [id, setId] = useState("")
  const [multiplier, setMultiplier] = useState("1")
  const add = () => {
    const normalized = id.trim()
    const amount = Number(multiplier)
    if (
      normalized === "" ||
      normalized in value ||
      !Number.isFinite(amount) ||
      amount <= 0 ||
      amount > 3
    ) {
      return
    }

    onChange({ ...value, [normalized]: amount })
    setId("")
    setMultiplier("1")
  }

  return (
    <fieldset className="grid gap-2">
      <legend className="text-sm font-medium">{legend}</legend>
      <p className="text-sm text-muted-foreground sm:min-h-10">{description}</p>
      {Object.entries(value).map(([currentId, currentMultiplier]) => (
        <div
          key={currentId}
          className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_7rem_8rem] sm:items-end"
        >
          <Label className="grid flex-1 gap-1">
            {itemLabel}
            <Input value={currentId} readOnly />
          </Label>
          <Label className="grid w-full gap-1">
            XP multiplier
            <Input
              type="number"
              min={0.01}
              max={3}
              step="any"
              value={currentMultiplier}
              onChange={(event) =>
                onChange({ ...value, [currentId]: Number(event.target.value) })
              }
            />
          </Label>
          <Button
            type="button"
            variant="destructive"
            size="sm"
            className="h-11 w-full"
            onClick={() => {
              const { [currentId]: _, ...remaining } = value
              onChange(remaining)
            }}
          >
            <Trash2 className="mr-2 size-4" />
            Remove
          </Button>
        </div>
      ))}
      <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_7rem_8rem] sm:items-end">
        <Input
          aria-label={`New ${itemLabel.toLowerCase()} bonus`}
          value={id}
          placeholder={`Discord ${itemLabel.toLowerCase()} ID`}
          onChange={(event) => setId(event.target.value)}
        />
        <Input
          aria-label="XP multiplier"
          type="number"
          min={0.01}
          max={3}
          step="any"
          value={multiplier}
          onChange={(event) => setMultiplier(event.target.value)}
        />
        <Button type="button" className="h-11 w-full" onClick={add}>
          <Plus className="mr-2 size-4" />
          Add bonus
        </Button>
      </div>
    </fieldset>
  )
}
